<template>
  <div class="animated fadeIn p-3">
    <b-row>
      <b-col xl="8" lg="12">
        <!-- Main Configuration Card -->
        <b-card>
          <template #header>
            <i class="fa fa-plus-circle text-info"></i> Add Pokemon
          </template>

          <div class="mb-4">
            <h6 class="text-info border-bottom border-secondary pb-2 mb-3 small uppercase letter-spacing-1">Configuration</h6>
            <b-row>
              <b-col md="9">
                <b-form-group label="Species Selection" label-class="small">
                  <search @change="pokemon = null" @selectedItem="selectedItem" placeholder="Search by name or ID..." :values="monsters"></search>
                </b-form-group>
              </b-col>
              <b-col md="3">
                <b-form-group label="Level" label-class="small">
                  <b-input-group size="sm">
                    <b-form-input v-model.number="level" type="number" max="100" min="1" class="text-center"></b-form-input>
                    <b-input-group-append is-text>LV</b-input-group-append>
                  </b-input-group>
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col md="6">
                <b-form-group label="Ability" label-class="small">
                  <b-form-select :disabled="pokemon == null" v-model="ability" size="sm">
                    <template v-if="pokemon != null">
                      <option v-for="(item, key) in pokemon.Ability" v-if="item !== 'None'" :key="key" :value="key">{{item}}</option>
                    </template>
                    <option v-else :value="0">---</option>
                  </b-form-select>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Nature" label-class="small">
                  <b-form-select v-model="nature" size="sm">
                    <option v-for="(item, key) in natures" :key="key" :value="key">{{item}}</option>
                  </b-form-select>
                </b-form-group>
              </b-col>
            </b-row>

            <h6 class="text-info border-bottom border-secondary pb-2 mt-4 mb-3 small uppercase letter-spacing-1">Moveset</h6>
            <b-row>
              <b-col v-for="i in 4" :key="'move'+i" md="6" class="mb-2">
                <b-form-group :label="'Move Slot #' + i" label-class="small text-muted mb-1">
                  <search :values="moves" v-model="selectedMoves[i-1]" placeholder="Search move..."></search>
                </b-form-group>
              </b-col>
            </b-row>
          </div>
        </b-card>
      </b-col>

      <b-col xl="4" lg="12">
        <!-- Preview -->
        <b-card no-body class="text-center mb-4 shadow-sm overflow-hidden">
          <div class="p-3">
            <div class="mb-3 d-flex align-items-center justify-content-center preview-area bg-dark-soft rounded">
              <img v-if="image" :src="image" class="img-fluid" style="max-height: 120px;" />
              <i v-else class="fa fa-paw fa-4x text-muted opacity-2"></i>
            </div>
            <h5 class="text-uppercase mb-2 small">{{pokemon ? pokemon.Name : 'Preview'}}</h5>
            <div class="d-flex justify-content-center pt-2 border-top border-secondary mt-2">
              <b-form-checkbox v-model="shiny" switch class="small uppercase" :class="{'shiny-rainbow': shiny}">Shiny Mode</b-form-checkbox>
            </div>
          </div>
        </b-card>

        <!-- Stats -->
        <b-card no-body>
          <template #header>
            <i class="fa fa-bar-chart text-info"></i> Stat Points
            <div class="card-actions">
              <span class="badge px-2" :class="totalEVs > 510 ? 'badge-danger' : 'badge-primary'">EV {{totalEVs}}/510</span>
            </div>
          </template>
          <table class="table table-sm table-striped mb-0 border-0">
            <thead>
              <tr class="text-info tiny-label">
                <th class="pl-3 border-0 py-2 uppercase">Stat</th>
                <th class="text-center border-0 py-2 uppercase" style="width: 75px;">IV</th>
                <th class="text-center border-0 py-2 uppercase" style="width: 75px;">EV</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, key) in names" :key="'stat'+key">
                <td class="pl-3 align-middle small">{{item}}</td>
                <td class="p-1">
                  <b-form-input v-model.number="ivs[key]" @input="checkIV(key)" type="number" size="sm" class="text-center" :state="ivs[key] <= 31 && ivs[key] >= 0"
                                :style="{ color: ivs[key] > 19 ? '#4dbd74' : (ivs[key] < 10 ? '#f86c6b' : 'inherit'), fontWeight: ivs[key] > 19 ? 'bold' : 'normal' }"></b-form-input>
                </td>
                <td class="p-1">
                  <b-form-input v-model.number="evs[key]" @input="checkEV(key)" type="number" size="sm" class="text-center" :state="!invalidEVs || evs[key] === 0"></b-form-input>
                </td>
              </tr>
            </tbody>
          </table>
          <b-progress :value="totalEVs" :max="510" :variant="totalEVs > 510 ? 'danger' : 'info'" height="3px" class="bg-dark rounded-0"></b-progress>
        </b-card>

        <!-- Servers -->
        <b-card class="mt-4">
          <template #header>
            <i class="fa fa-server text-info"></i> Target Servers
          </template>
          <div class="d-flex flex-wrap pt-1 mb-3">
            <b-form-checkbox v-for="ser in servers" v-if="!serverObj[ser].readonly" :key="ser" 
                             :value="ser" v-model="checked" class="mr-3 mb-2 text-capitalize small">
              {{ser}}
            </b-form-checkbox>
          </div>
          <b-btn :disabled="pokemon == null || checked.length === 0 || invalidIVs || invalidEVs" 
                 @click="spawnPokemon" variant="primary" block size="sm" class="py-2 mt-2 shadow uppercase font-weight-bold">
            <i class="fa fa-flash mr-1"></i> Add Pokemon
          </b-btn>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
  import Search from './Search'
  import Monsters from './_monsters.js'
  import Natures from './_natures.js'
  import Moves from './_moves.js'
  import Servers from '../../_servers'
  import { mapGetters } from 'vuex'
  import iziToast from 'izitoast'

  export default {
    name: 'PokemonAdd',
    components: {
      Search
    },
    data () {
      return {
        pokemon: null,
        image: '',
        names: ['ATK', 'DEF', 'SPD', 'SPATK', 'SPDEF', 'HP'],
        evs: [0, 0, 0, 0, 0, 0],
        ivs: [31, 31, 31, 31, 31, 31],
        selectedMoves: [null, null, null, null],
        monsters: Monsters,
        natures: Natures,
        checked: [],
        servers: Object.keys(Servers),
        serverObj: Servers,
        shiny: false,
        level: 100,
        ability: 0,
        moves: Moves,
        nature: 8
      }
    },
    computed: {
      ...mapGetters(['pokemonError']),
      totalEVs () {
        return this.evs.reduce((a, b) => Number(a) + Number(b), 0)
      },
      invalidEVs () {
        return this.totalEVs > 510 || this.evs.some(v => v > 252 || v < 0)
      },
      invalidIVs () {
        return this.ivs.some(v => v > 31 || v < 0)
      }
    },
    methods: {
      checkIV (key) {
        if (this.ivs[key] > 31) this.$set(this.ivs, key, 31)
        if (this.ivs[key] < 0) this.$set(this.ivs, key, 0)
      },
      checkEV (key) {
        if (this.evs[key] > 252) this.$set(this.evs, key, 252)
        if (this.evs[key] < 0) this.$set(this.evs, key, 0)
      },
      spawnPokemon () {
        let moves = this.selectedMoves.filter(item => item !== null)
        this.emit('addPokemon', {
          'monster_id': this.pokemon.ID,
          'shiny': this.shiny,
          'level': Number(this.level),
          'moves': moves,
          'nature': this.nature,
          'ability': Number(this.ability) + 1,
          'ivs': this.ivs.map(Number),
          'evs': this.evs.map(Number),
          'servers': this.checked
        })
      },
      resetForm () {
        this.evs = [0, 0, 0, 0, 0, 0]
        this.ivs = [31, 31, 31, 31, 31, 31]
        this.selectedMoves = [null, null, null, null]
        this.checked = []
        this.nature = 8
        this.shiny = false
        this.pokemon = null
        this.level = 100
        this.ability = 0
        this.image = null
      },
      selectedItem (e) {
        this.pokemon = e
        this.updateImage()
        if (e.Ability && e.Ability.length > 0) {
          for (let i = 0; i < e.Ability.length; i++) {
            if (e.Ability[i] !== 'None') {
              this.ability = i
              break
            }
          }
        }
      },
      updateImage () {
        if (!this.pokemon) return
        const baseUrl = 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/'
        const shinyPath = this.shiny ? 'shiny/' : ''
        this.image = `${baseUrl}${shinyPath}${this.pokemon.ID}.png`
      }
    },
    watch: {
      shiny: function () {
        this.updateImage()
      },
      pokemonError (e) {
        if (e.error === 1) {
          iziToast.success({ title: 'Success', message: 'Pokemon added successfully!' })
          this.resetForm()
        } else if (e.error === 2) {
          iziToast.error({ title: 'User Online', message: 'You must be offline to receive Pokemon.' })
        } else if (e.error === 10) {
          iziToast.info({ title: 'Request Sent', message: 'The request has been forwarded to admins.' })
          this.resetForm()
        } else {
          iziToast.error({ title: 'Error', message: 'Operation failed.' })
        }
      }
    }
  }
</script>

<style scoped>
.preview-area { min-height: 140px; }
.bg-dark-soft { background-color: rgba(0,0,0,0.1); }
.uppercase { text-transform: uppercase; }
.letter-spacing-1 { letter-spacing: 1px; }
.tiny-label { font-size: 0.65rem; letter-spacing: 1px; }
.card-actions { float: right; display: flex; }

/* Fix for light background on disabled inputs/selects in dark theme */
.custom-select:disabled, .form-control:disabled {
  background-color: rgba(0,0,0,0.2) !important;
  color: #73818f !important;
  border-color: rgba(255,255,255,0.1) !important;
  opacity: 0.7;
}
</style>